import React, { Dispatch, SetStateAction, useEffect, useState } from "react";

import { Attribute, AttributeData_String, AttributeType, Maybe, MdmDeviceDto, MdmObjectDto } from "gql/generated/types";
import { Accordion, AccordionTab } from "primereact/accordion";
import InputLabelWrapper from "components/basic/input-label-wrapper/InputLabelWrapper";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";

type AdditionalInfoProps<T> = {
  attributes: (Attribute | null)[];
  objectState: T;
  setObjectState: Dispatch<SetStateAction<T>>
};
const AdditionalInfo = <T extends MdmObjectDto | Partial<MdmDeviceDto>>({
  attributes,
  objectState,
  setObjectState,
}: AdditionalInfoProps<T>) => {
  const map = new Map(objectState.customFields?.map(field => [field?.key, field?.value]));
  const [attributeMap, setAttributeMap] = useState<Map<Maybe<string> | undefined, Maybe<AttributeData_String> | undefined>>(map);

  useEffect(() => {
    setObjectState(prev => ({
      ...prev,
      customFields: [...attributeMap].map(([key, value]) => ({
        key,
        value,
      })),
    }));
  }, [attributeMap]);

  const attributeGroups = attributes.reduce((acc, curr) => {
    const parent = curr?.parent?.toString();
    if (!parent || !curr) return acc;
    const parentAttributes = acc.get(parent);
    if (!parentAttributes) return acc.set(parent, [curr]);
    return acc.set(parent, [...parentAttributes, curr]);
  }, new Map<string, Attribute[]>());

  const renderInput = (body: Attribute) => {
    switch (body.type) {
      case AttributeType.String:
        return (
          <InputText
            value={attributeMap.get(body.id)?.value || ""}
            onChange={e =>
              setAttributeMap(
                prev => new Map(prev.set(body.id, { type: body.type, value: e.target.value }))
              )
            }
            className="p-inputtext-sm"
            name={body.title}
          />
        );
      case AttributeType.Number:
        return (
          <InputNumber
            value={parseFloat(attributeMap.get(body.id)?.value!) ?? 0}
            onChange={e =>
              setAttributeMap(
                prev => new Map(prev.set(body.id, { type: body.type, value: `${e.value}` }))
              )
            }
            className="p-inputtext-sm"
            name={body.title}
          />
        );
      case AttributeType.Boolean:
        return (
          <InputSwitch
            checked={attributeMap.get(body.id)?.value === "true"}
            onChange={e =>
              setAttributeMap(
                prev => new Map(prev.set(body.id, { type: body.type, value: `${e.value}` }))
              )
            }
            className="p-inputtext-sm mt-3 align-self-center"
            name={body.title}
          />
        )
      default:
        return null;
    }
  }

  const nodes = [...attributeGroups].map(([key, value]) => (
    <AccordionTab key={key} header={key}>
      <div className="grid align-items-end">
        {value.map((attr: Attribute) => (
          <InputLabelWrapper key={attr.id} labelName={attr.title} col={2}>
            {renderInput(attr)}
          </InputLabelWrapper>
        ))}
      </div>
    </AccordionTab>
  ));

  return <Accordion className="w-full mt-3">{nodes}</Accordion>;
};

export default AdditionalInfo;